import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DataTable from '../../components/ui/DataTable';
import DeleteConfirmationModal from '../../components/ui/DeleteConfirmationModal';
import AssignStaffModal from '../../components/ui/AssignStaffModal';
import QuotationDetail from './QuotationDetail';
import { 
  listQuotations, 
  getQuotation,
  getQuotationStats,
  updateQuotationStatus,
  assignStaff,
  approveQuotation,
  rejectQuotation,
  archiveQuotation,
  updateQuotationFees
} from '../../services/quotations';
import toast from 'react-hot-toast';

const Quotation = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get id from URL params
  const [activeTab, setActiveTab] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAssignStaffModalOpen, setIsAssignStaffModalOpen] = useState(false);
  const [selectedLicenceId, setSelectedLicenceId] = useState(null);
  const [viewMode, setViewMode] = useState(id ? 'detail' : 'list'); // 'list' or 'detail'
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [quotations, setQuotations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load quotation detail if id is in URL
  useEffect(() => {
    if (id) {
      loadQuotationDetail(id);
    } else {
      loadQuotations();
      loadStats();
    }
  }, [id, activeTab, searchTerm]);

  const loadQuotationDetail = async (quotationId) => {
    try {
      setLoading(true);
      const response = await getQuotation(quotationId);
      if (response.success && response.data) {
        setSelectedQuotation(response.data);
        setViewMode('detail');
      } else {
        toast.error('Failed to load quotation details');
        navigate('/quotations');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load quotation details');
      navigate('/quotations');
    } finally {
      setLoading(false);
    }
  };

  const loadQuotations = async () => {
    try {
      setLoading(true);
      const response = await listQuotations({
        status: activeTab === 'All' ? undefined : activeTab,
        search: searchTerm || undefined,
        page: currentPage,
        limit: 20
      });
      
      if (response.success) {
        setQuotations(response.data || []);
      } else {
        toast.error(response.message || 'Failed to load quotations');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load quotations');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await getQuotationStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const statsCards = [
    { 
      label: 'Total Requests', 
      count: stats?.total || 0, 
      iconType: 'document',
      iconColor: '#73827c'
    },
    { 
      label: 'Ready for Signature', 
      count: stats?.readyForSignature || 0, 
      iconType: 'document',
      iconColor: '#73827c'
    },
    { 
      label: 'Under Review', 
      count: stats?.underReview || 0, 
      iconType: 'document',
      iconColor: '#73827c'
    },
    { 
      label: 'Archived', 
      count: stats?.archived || 0, 
      iconType: 'document',
      iconColor: '#73827c'
    }
  ];

  const tabs = [
    { name: 'All', count: stats?.total || 0 },
    { name: 'Pending', count: stats?.pending || 0 },
    { name: 'Under Review', count: stats?.underReview || 0 },
    { name: 'Ready for Signature', count: stats?.readyForSignature || 0 },
    { name: 'Archived', count: stats?.archived || 0 }
  ];

  // Transform API data to table format
  const allLicences = quotations.map((q) => {
    const fees = q.fees || {};
    const subtotal = Number(fees.totalPrice) || 0;
    const hasVat = fees.vatApplicable && (Number(fees.vatAmount) || 0) > 0;
    const vatAmount = hasVat ? Number(fees.vatAmount) : 0;
    const grandTotal = hasVat ? (Number(fees.grandTotal) || subtotal + vatAmount) : subtotal;
    return {
    id: q._id,
    _id: q._id,
    status: q.status,
    date: q.submittedAt ? new Date(q.submittedAt).toLocaleDateString('en-GB') : 'N/A',
    organization: q.org?.name || 'N/A',
    licenceName: q.coaId?.name || 'N/A',
    name: q.contact?.fullName || 'N/A',
    product: 'Quotation',
    amount: `£${subtotal.toFixed(2)}`,
    vat: hasVat ? `£${vatAmount.toFixed(2)} (${Math.round((fees.vatRate || 0.2) * 100)}%)` : '',
    total: `£${grandTotal.toFixed(2)}`,
    quote: q.status,
    reviewer: q.reviewerId?.fullName || 'N/A',
    approver: q.approverId?.fullName || 'N/A',
    refNumber: q.refNumber || 'N/A',
    quotation: q,
    customerActionKey: q.customerAction?.key || ''
    };
  });

  const columns = [
    { key: 'refNumber', label: 'Ref Number', render: (value) => <span className="text-[#73827c] font-medium">{value}</span> },
    { key: 'date', label: 'Date', render: (value) => <span className="text-[#73827c]">{value}</span> },
    { key: 'organization', label: 'Organisation', render: (value) => <span className="text-[#73827c]">{value}</span> },
    { key: 'licenceName', label: 'Licence Name', render: (value) => <span className="text-[#73827c]">{value}</span> },
    { key: 'name', label: 'Name', render: (value) => <span className="text-[#73827c]">{value}</span> },
    { key: 'product', label: 'Product', render: (value) => <span className="text-[#73827c]">{value}</span> },
    { key: 'amount', label: 'Amount (ex. VAT)', render: (value) => <span className="text-[#73827c] font-medium">{value}</span> },
    { key: 'vat', label: 'VAT', render: (value) => value ? <span className="text-[#73827c]">{value}</span> : <span className="text-gray-400">—</span> },
    { key: 'total', label: 'Total (inc. VAT)', render: (value) => <span className="text-[#222f2a] font-semibold">{value}</span> },
    { key: 'customerActionKey', label: 'Customer Action', render: (value) => value ? <span className="px-2.5 py-1 inline-flex text-xs font-medium rounded-full 
       bg-red-500 text-[rgb(164,53,53)]">{value}</span> : <span className="text-gray-400">—</span> },
    { key: 'quote', label: 'Quote', render: (value) => (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm ${
        value === 'Pending' ? 'bg-[#fff8e6] text-[#f4a340]'
        : value === 'Granted' || value === 'Approved' ? 'bg-[#f0fff2] text-[#40c191]'
        : value === 'Rejected' ? 'bg-[#ffe6e6] text-[#f56565]'
        : 'bg-[#e6f3ff] text-[#3182ce]'
      }`}>
        <span className={`w-1.5 h-1.5 rounded-full ${
          value === 'Pending' ? 'bg-[#f4a340]'
          : value === 'Granted' || value === 'Approved' ? 'bg-[#40c191]'
          : value === 'Rejected' ? 'bg-[#f56565]'
          : 'bg-[#3182ce]'
        }`} />
        {value}
      </span>
    ) },
    { key: 'reviewer', label: 'Reviewer', render: (value) => <span className="text-[#73827c]">{value}</span> },
    { key: 'approver', label: 'Approver', render: (value) => <span className="text-[#73827c]">{value}</span> },
  ];

  const actionMenuItems = [
    { label: 'View Details', action: 'view', icon: (<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10.3866 8.00001C10.3866 9.31334 9.31995 10.38 7.99995 10.38C6.67995 10.38 5.61328 9.31334 5.61328 8.00001C5.61328 6.68668 6.67995 5.62001 7.99995 5.62001C9.31995 5.62001 10.3866 6.68668 10.3866 8.00001Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M8.00004 13.5133C10.3534 13.5133 12.5467 12.1267 14.0734 9.72668C14.6734 8.78668 14.6734 7.20668 14.0734 6.26668C12.5467 3.86668 10.3534 2.48001 8.00004 2.48001C5.64671 2.48001 3.45337 3.86668 1.92671 6.26668C1.32671 7.20668 1.32671 8.78668 1.92671 9.72668C3.45337 12.1267 5.64671 13.5133 8.00004 13.5133Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>) },
    { label: 'Edit Quotation', action: 'edit', icon: (<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M7.33333 1.33334H6C2.66667 1.33334 1.33333 2.66668 1.33333 6.00001V10C1.33333 13.3333 2.66667 14.6667 6 14.6667H10C13.3333 14.6667 14.6667 13.3333 14.6667 10V8.66668" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M10.6933 2.01333L5.44 7.26666C5.24 7.46666 5.04 7.86 5 8.14666L4.71333 10.1533C4.60667 10.8733 5.12667 11.3867 5.84667 11.2867L7.85333 11C8.13333 10.96 8.52667 10.76 8.73333 10.56L13.9867 5.30666C14.8933 4.39999 15.32 3.34666 13.9867 2.01333C12.6533 0.679997 11.6 1.10666 10.6933 2.01333Z" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/><path d="M9.94 2.76666C10.3867 4.36 11.64 5.61333 13.24 6.06666" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/></svg>) },
    { label: 'Assign Staff', action: 'assign', icon: (<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 8C10.21 8 12 6.21 12 4C12 1.79 10.21 0 8 0C5.79 0 4 1.79 4 4C4 6.21 5.79 8 8 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M0 16V14C0 12.9 0.9 12 2 12H14C15.1 12 16 12.9 16 14V16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>) },
  ];

  const handleAction = async (action, licence) => {
    if (action === 'view' || action === 'edit') {
      // Navigate to detail page using URL
      const quotationId = licence.id || licence._id;
      navigate(`/quotations/${quotationId}`);
    } else if (action === 'assign') {
      setSelectedLicenceId(licence.id || licence._id);
      setIsAssignStaffModalOpen(true);
    }
  };

  const handleAssignStaff = async (reviewerId, approverId) => {
    try {
      await assignStaff(selectedLicenceId, reviewerId, approverId);
      toast.success('Staff assigned successfully');
      setIsAssignStaffModalOpen(false);
      loadQuotations();
    } catch (err) {
      toast.error(err.message || 'Failed to assign staff');
    }
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedQuotation(null);
    navigate('/quotations');
  };

  const filteredLicences = allLicences.filter(licence => {
    const matchesTab = activeTab === 'All' || licence.status === activeTab;
    const matchesSearch = searchTerm === '' || 
      licence.organization.toLowerCase().includes(searchTerm.toLowerCase()) ||
      licence.licenceName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      licence.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      licence.refNumber.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesTab && matchesSearch;
  });

  if (viewMode === 'detail' && selectedQuotation) {
    return (
      <QuotationDetail 
        quotationId={selectedQuotation._id || selectedQuotation.id}
        quotation={selectedQuotation}
        onBack={handleBackToList} 
      />
    );
  }

  return (
    <div className="max-w-[1200px] mx-auto space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Quotation Management</h1>
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M4.16667 5.83333H15.8333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M6.66667 10H13.3333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M9.16667 14.1667H10.8333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            Filter
          </button>

          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M17.5 17.5L13.875 13.875" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <input type="text" placeholder="Search quotations..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent w-64" />
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsCards.map((card, index) => (
          <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{card.label}</p>
                <p className="text-2xl font-bold text-gray-900">{card.count}</p>
              </div>
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                <div className="w-6 h-6 bg-gray-300 rounded"></div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs + Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button key={tab.name} onClick={() => setActiveTab(tab.name)} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === tab.name ? 'border-emerald-500 text-emerald-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
                {tab.name}
                <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2.5 rounded-full text-xs">{tab.count}</span>
              </button>
            ))}
          </nav>
        </div>
        <div className="p-6">
          <DataTable columns={columns} data={filteredLicences} actionMenuItems={actionMenuItems} onAction={handleAction} />
        </div>
      </div>

      {/* Assign Staff Modal */}
      <AssignStaffModal 
        isOpen={isAssignStaffModalOpen} 
        onClose={() => setIsAssignStaffModalOpen(false)} 
        licenceId={selectedLicenceId}
        onAssign={handleAssignStaff}
      />
    </div>
  );
};


export default Quotation;


